<template>
  <div class="select-media">
    <div v-if="multiple? (medias && medias.length): media" class="preview mb-5">
      <slot>
        <div class="well border-left border-left-primary">
          <a class="delete" href="#" @click.prevent="clear" :title="multiple? 'Tout enlever': 'Enlever ce fichier'"><i class="icon-cross pull-right"></i></a>
          <div v-if="multiple">
            <div v-if="image">
              <div v-for="media in medias" :key="media.id" class="img-thumbnail img-rounded mr-5 mb-5">
                <div class="thumb">
                  <img :src="media | media('admin-thumbnail')" width="95" height="70" :alt="media.alt || media.name" />
                  <p class="no-margin text-overflow text-center"><strong>{{media.name}}</strong><br /><small class="text-muted">{{media.length | filesize}}</small></p>
                  <div class="caption-overflow" v-if="editable">
                    <span>
                        <button type="button" @click="openEditMedia = media" class="btn border-white text-white btn-flat btn-icon btn-rounded"><i class="icon-pencil"></i></button>
                        <button type="button" @click="openDeleteMedia = media" class="btn border-white text-white btn-flat btn-icon btn-rounded"><i class="icon-trash"></i></button>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              Fichier(s) sélectionné(s) :
              <ul>
                <li v-for="media in medias">
                  <strong>{{media.name}}</strong>
                  <span v-if="editable">
                      <button type="button" @click="openEditMedia = media" class="btn btn-icon"><i class="icon-pencil"></i></button>
                      <button type="button" @click="openDeleteMedia = media" class="btn btn-icon"><i class="icon-trash"></i></button>
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div v-else>
            <div v-if="image">
              <img :src="media | media('admin-preview')" :alt="media.alt || media.name" class="img-thumbnail img-rounded">
              <p class="no-margin text-overflow text-center"><strong>{{media.name}}</strong><br /><small class="text-muted">{{media.length | filesize}}</small></p>
            </div>
            <span v-else>Fichier sélectionné : <strong>{{media.name}}</strong></span>
          </div>
        </div>
      </slot>
    </div>
    <div>
      <form-upload :disabled="!!loading" :process-upload="processUpload" browse-label="Parcourir..." :empty-label="multiple? 'Envoyer des fichiers...': 'Envoyer un fichier'" :multiple="multiple"></form-upload>
    </div>
    <p v-show="error" class="error">{{error}}</p>

    <modal v-if="openEditMedia" :title="`Édition ${image ? 'de l\'image': 'du fichier'} ${openEditMedia.name}`" @close="openEditMedia = null" size="sm">
      <p v-show="image">Sélectionner la nouvelle image pour remplacer <strong>{{openEditMedia.name}}</strong>&hellip;</p>
      <p v-show="!image">Sélectionner le nouveau fichier pour remplacer <strong>{{openEditMedia.name}}</strong>&hellip;</p>
      <form-upload :disabled="!!loading" :process-upload="processUploadMedia" browse-label="Parcourir..." empty-label="Envoyer un fichier"></form-upload>
    </modal>

    <modal v-if="openDeleteMedia" :title="`Suppression ${image? 'de l\'image': 'du fichier'} ${openDeleteMedia.name}`" @close="openDeleteMedia = null" size="sm">
      <p v-show="image">Voulez-vous supprimer définitivement l'image <strong>{{openDeleteMedia.name}}</strong> partout où elle est utilisée&nbsp;?</p>
      <p v-show="!image">Voulez-vous supprimer définitivement le fichier <strong>{{openDeleteMedia.name}}</strong> partout où il est utilisé&nbsp;?</p>
      <button type="button" class="btn btn-warning" @click="processDeleteMedia">Je confirme la suppression</button>
    </modal>
  </div>
</template>

<style type="text/css">
  .select-media .preview {
    position: relative;
  }

  .select-media .preview .delete {
    position: absolute;
    right: 10px;
    top: 10px;
  }
</style>

<script>
  import gql from 'graphql-tag';

  const returnFields = `id name mime length`;

  const MediaFetchByIdQuery = gql`query($id: ID!) {
    media: mediaFetchById(id: $id) {
      ${returnFields}
    }
  }`;
  const MediaFetchByIdsQuery = gql`query MediasById($ids: [ID]) {
    medias: mediasFetchByIds(ids: $ids) {
      ${returnFields}
    }
  }`;
  const MediaUploadMutation = gql`mutation ($files: [Upload]) {
    results: mediaUpload(files: $files) {
      ${returnFields}
    }
  }`;
  const MediaReplaceMutation = gql`mutation ($id: ID!, $file: Upload) {
    result: mediaReplace(id: $id, file: $file) {
      ${returnFields}
    }
  }`;
  const MediaRemoveMutation = gql`mutation ($id: ID!) {
    result: mediaRemove(id: $id)
  }`;

  export default {
    components: {
      Modal: () => import("package-common-vuejs/src/components/Modal"),
      FormUpload: () => import('package-common-vuejs/src/components/form/FormUpload'),
      FormTypeahead: () => import('package-common-vuejs/src/components/form/FormTypeahead'),
    },
    props: {
      value: [Array, String],
      limit: {type: Number, default: 10},
      multiple: Boolean,
      image: Boolean,
      editable: Boolean,
    },
    data() {
      return {
        media: null,
        medias: [],
        loading: 0,
        error: null,
        openEditMedia: null,
        openDeleteMedia: null,
      };
    },
    computed: {
      mediasIds() {
        return [...(this.value || [])];
      },
      dataset() {
        return {
          source: (query, syncResults, asyncResults) => {
            return this.searchMedia(query)
              .then(asyncResults);
          },
          limit: 50,
          display: ({name, mime, length}) => `${name}`,
          templates: {
            notFound: `<p class="no-margin text-muted pl-10 pr-10 pt-5 pb-5">Aucun résultat</p>`,
            suggestion: (media) => {
              const {name, mime, length} = media;
              return `<div>
                ${this.image? `<figure class="pull-left position-left"><img src="${this.$options.filters.media(media, 'manager-thumbnail')}" width="54" height="40" alt="${media.alt || media.name}" class="img-thumbnail img-rounded" /></figure>`: ``}
                <p class="no-margin"><strong>${name}</strong></p>
                <p class="no-margin text-muted"><small><em>${mime} - ${this.$options.filters.filesize(length)}</em></small></p>
              </div>`;
            },
          },
        };
      },
    },
    apollo: {
      media: {
        query: MediaFetchByIdQuery,
        fetchPolicy: 'cache-and-network',
        skip() {
          return this.multiple || !this.value;
        },
        variables() {
          return {id: this.value};
        }
      },
      medias: {
        query: MediaFetchByIdsQuery,
        fetchPolicy: 'cache-and-network',
        skip() {
          return !this.multiple || !this.mediasIds;
        },
        variables() {
          return {ids: this.mediasIds};
        }
      },
    },
    methods: {
      clear() {
        const valueToEmit = this.multiple ? [] : null;
        this.media = null;
        this.medias = [];
        this.$emit('input', valueToEmit);
        this.$emit('change', valueToEmit);
      },
      async processUpload(files) {
        if (!files.length) return;
        this.loading++;
        this.error = null;
        try {
          const {data} = await this.$apollo.mutate({
            mutation: MediaUploadMutation,
            variables: {
              files,
            },
          });
          let valueToEmit;
          if (this.multiple) {
            this.medias.concat(...data.results); // shortcut
            valueToEmit = [...(this.value || []), ...data.results.map(({id}) => id)];
          } else {
            this.media = data.results[0]; // shortcut
            valueToEmit = data.results[0].id;
          }
          this.$emit('input', valueToEmit);
          this.$emit('change', valueToEmit);
          this.loading--;
        } catch (err) {
          console.error(err);
          this.error = err.message;
          this.loading--;
        }
      },
      async processUploadMedia([file]) {
        if (!file || !this.openEditMedia) return;
        this.loading++;
        this.error = null;
        try {
          const {data} = await this.$apollo.mutate({
            mutation: MediaReplaceMutation,
            variables: {
              id: this.openEditMedia.id,
              file,
            },
          });
          if (this.multiple) {
            this.medias = this.medias.map(media => media.id !== this.openEditMedia.id? media: data.result);
          } else {
            this.media = data.result; // shortcut
          }
          this.openEditMedia = null;
          this.loading--;
        } catch (err) {
          console.error(err);
          this.error = err.message;
          this.loading--;
        }
      },
      async processDeleteMedia() {
        if (!this.openDeleteMedia) return;
        this.loading++;
        try {
          const {data} = await this.$apollo.mutate({
            mutation: MediaRemoveMutation,
            variables: {
              id: this.openDeleteMedia.id,
            },
          });
          let valueToEmit;
          if (this.multiple) {
            this.medias = this.medias.filter(media => media.id !== this.openDeleteMedia.id? media: data.result);
            valueToEmit = (this.value || []).filter(id => id !== this.openDeleteMedia.id);
          } else {
            this.media = null;
            valueToEmit = data.results[0].id;
          }
          this.$emit('input', valueToEmit);
          this.$emit('change', valueToEmit);
          this.openDeleteMedia = null;
          this.loading--;
        } catch (err) {
          console.error(err);
          this.error = err.message;
          this.loading--;
        }
      }
    },
  }
</script>



// WEBPACK FOOTER //
// src/components/FormFileUpload.vue